import clsx from "clsx";
import { KeyboardEvent, useEffect, useState } from "react";

import { Country } from "@smart/bridge-types-basic";
import {
  Icon,
  IconName,
  TimeFilter,
  TimePeriod,
  ProgressCircle,
  TimeRange,
  Link,
} from "@smart/itops-sb-design-system-dom";

import { GqlSubmissionSummary } from "../../types";
import { filterByPeriod } from "../utils/time-filters";

type WidgetProps = {
  title: string;
  submissions: GqlSubmissionSummary[];
  actionLink?: string;
  iconName: IconName;
  iconBgColour: string;
  filterByField?: "createdAt" | "updatedAt" | "completedAt";
  defaultFilter?: TimePeriod;
  filters?: TimePeriod[];
  country: Country;
  filterStorageKey: string;
  loading?: boolean;
  onFilterValueChanged?: (period: TimePeriod, range?: TimeRange) => void;
  onClick: () => void;
};

const stopPropagation = (event: { stopPropagation: () => void }) => {
  event.stopPropagation();
};

export const Widget = ({
  title,
  submissions,
  actionLink,
  iconName,
  iconBgColour,
  filterByField,
  defaultFilter = "thisWeek",
  filters,
  country,
  filterStorageKey,
  loading,
  onFilterValueChanged,
  onClick,
}: WidgetProps) => {
  const [filter, setFilter] = useState<{
    period: TimePeriod;
    range?: TimeRange;
  }>();
  const [filteredSubmissions, setFilteredSubmissions] =
    useState<GqlSubmissionSummary[]>(submissions);

  useEffect(() => {
    if (filter && filterByField) {
      const filtered = filterByPeriod({
        submissions,
        period: filter.period,
        country,
        filterByField,
        range: filter.range,
      });
      setFilteredSubmissions(filtered);
    }
  }, [filter, submissions, country, filterByField]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={clsx(
        "flex flex-col flex-1 py-[1.6rem] px-[2.4rem] bg-white rounded min-h-[11.2rem] gap-medium max-h-96 min-w-48 cursor-pointer hover:bg-neutral-20 transition-colors",
      )}
      data-testid={`${title}-widget`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <div className="text-paragraph font-semibold truncate">{title}</div>
      <div className="flex justify-between items-center flex-wrap gap-medium">
        <div className="flex gap-medium items-center max-h-[8.5rem] flex-wrap">
          {loading ? (
            <div className="h-[3.8rem]">
              <ProgressCircle size="xSmall" />
            </div>
          ) : (
            <div className="flex gap-medium items-center">
              <div
                className={clsx(
                  "rounded w-[4rem] h-[4rem] flex justify-center items-center",
                  iconBgColour,
                )}
              >
                <Icon name={iconName} className="w-[2.2rem] h-[2.2rem]" />
              </div>
              <div className="text-h2 font-semibold">
                {filterByField
                  ? filteredSubmissions.length
                  : submissions.length}
              </div>
            </div>
          )}
          {filterByField && (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div onClick={stopPropagation} onKeyDown={stopPropagation}>
              <TimeFilter
                isButton
                defaultFilter={defaultFilter}
                filters={filters}
                onChange={(period, range) => {
                  if (onFilterValueChanged) onFilterValueChanged(period, range);
                  setFilter({ period, range });
                }}
                filterStorageKey={filterStorageKey}
                dataTestId={`${title}-filter`}
              />
            </div>
          )}
        </div>
        {actionLink && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div onClick={stopPropagation} onKeyDown={stopPropagation}>
            <Link
              variant="default"
              text="Learn More"
              href={actionLink}
              target="_blank"
              className="text-ui"
            />
          </div>
        )}
      </div>
    </div>
  );
};
